import { useCallback, useEffect } from "react";
import { useQuery } from "@apollo/client/react";
import { MyCompaniesDocument } from "@/graphql/generated/operations";
import { useAuthStore } from "@/stores/auth-store";
import { useCompanyContextStore } from "@/stores/company-context-store";

// Resolves the acting-company context (the company account model) from the id
// the browser holds into something that may actually be shown.
//
// THE RESOLUTION IS THE POINT. `activeCompanyId` is a browser-held value on a transport whose proxy
// does no authorization (the BFF injects the bearer and forwards verbatim), so it is
// attacker-controlled by construction and cannot be believed. It is matched against `myCompanies` —
// genesis's answer to "which company_membership rows point at the caller", derived from the token
// subject and taking no person argument — and a context exists only for a row that list contains.
// Everything on screen therefore comes from genesis: the name, and the fact that the person is a
// member at all.
//
// An id with no matching row resolves to NOTHING and is cleared. Setting the store to a company one
// is not a member of is consequently not an escalation and not even a cosmetic one: it produces no
// bar, no name and no capability. What it can never do is authorize, because no write reads it —
// per-company mutations carry their target from the route and genesis re-checks the membership row
// on every call (@external-adr GENESIS-0023-company-membership-is-the-per-company-authorization).
//
// WHO MAY ENTER: ADMINS, AND SINCE CONCEPT v10 ONLY ADMINS. the company account model says of a plain member that
// "die Firma taucht in seinem Kontext-Switch auf", and the company page puts the button in the company
// page header "Für Admins der Firma"; the two used to be followed literally and separately, so the
// switcher at /c offered every membership while the page offered it to admins. v10 reconciled them
// in the admin's favour (0002 §5): a member's companies still APPEAR in their list, but a member
// cannot enter. What the context may DO is bounded by §6 to viewing and editing the company and
// managing its members — all admin-only — which is why a member's context was a mode in which
// nothing worked. Genesis reports this membership authority through Company.canAdminister.

export interface ActiveCompanyContext {
  id: string;
  /** genesis's stored name, never a value the browser was holding. */
  name: string;
  /** The caller's own membership role. An open string (the company account model) — never switch on it exhaustively. */
  role: string;
  /** Backend-reported Company authority; independent of platform Roles. */
  canAdminister: boolean;
}

export interface UseCompanyContextResult {
  /**
   * The resolved context, or `null`.
   *
   * `null` covers three states on purpose and callers must not try to tell them apart: acting as
   * oneself, a stored id genesis has not answered about yet, and a stored id with no membership row
   * behind it. All three mean the same thing to anything that renders — there is no company to
   * name — and a flag distinguishing "resolving" from "none" would only invite a caller to show
   * something during the one window where nothing has been confirmed.
   */
  active: ActiveCompanyContext | null;
  enter: (companyId: string) => void;
  leave: () => void;
}

export function useCompanyContext(): UseCompanyContextResult {
  const activeCompanyId = useCompanyContextStore((state) => state.activeCompanyId);
  const enter = useCompanyContextStore((state) => state.enterCompany);
  const leave = useCompanyContextStore((state) => state.leaveCompany);
  const authenticated = useAuthStore((state) => state.status === "authenticated");

  // Only asked when there is something to resolve. A person who never enters a company context
  // costs no query, which is why this hook can sit in the app shell on every page.
  const { data, loading } = useQuery(MyCompaniesDocument, {
    skip: !authenticated || activeCompanyId === null,
  });

  const memberships = data?.myCompanies.memberships;
  // ADMIN ONLY, and enforced here rather than at each button (concept v10, 0002 §5). Every capability
  // a company context has is already admin-only, so for a `member` the context was a mode in which
  // nothing worked — and in the composer it was actively harmful, attaching the company to every post
  // and having genesis refuse them all, so a member in context could publish nothing at all.
  //
  // Resolving a member's stored id to NOTHING also makes the self-healing effect below do the rest:
  // an admin demoted while the tab is open stops acting as the company on the next answer, exactly as
  // if the membership had been revoked. A check at each entry point could not do that.
  const match =
    memberships?.find(
      (membership) =>
        membership.company.id === activeCompanyId && membership.company.canAdminister,
    ) ?? null;

  // Self-healing, and the only writer of the store outside the two actions: a membership can be
  // revoked while the tab is open, and genesis would then refuse everything the context implies
  // while spark kept naming the company. Guarded on a settled answer — `loading` covers the refetch
  // that the cache reset itself triggers, and a query that errored leaves `memberships` undefined,
  // where dropping the context would punish a network blip.
  const stale = activeCompanyId !== null && !loading && memberships !== undefined && match === null;
  useEffect(() => {
    if (stale) leave();
  }, [stale, leave]);

  const enterCompany = useCallback((companyId: string) => enter(companyId), [enter]);

  return {
    active: match
      ? { id: match.company.id, name: match.company.name, role: match.role, canAdminister: match.company.canAdminister }
      : null,
    enter: enterCompany,
    leave,
  };
}
